<script lang="ts">
	import { mutation } from "@api/graphql.js";
	import sendNewsOtpMutation from "@api/mutations/sendNewsOtp.graphql";
	import verifyNewsOtpMutation from "@api/mutations/verifyNewsOtp.graphql";
	import Alert from "@components/Alert.svelte";
	import ArticleStructuredData from "@components/ArticleStructuredData.svelte";
	import ContentBlocks from "@components/ContentBlocks.svelte";
	import HugeHeading from "@components/HugeHeading.svelte";
	import RecaptchaDisclaimer from "@components/RecaptchaDisclaimer.svelte";
	import Button from "@components/common/Button.svelte";
	import Formatted from "@components/common/Formatted.svelte";
	import Media from "@components/common/Media.svelte";
	import Form from "@components/form/Form.svelte";
	import LabelledInput from "@components/form/LabelledInput.svelte";
	import Layout from "@components/layout/Layout.svelte";
	import { onMount } from "svelte";
	import { ButtonVariant } from "@core/schema/ButtonVariant.js";
	import type { SendNewsOtpInput, VerifyNewsOtpInput } from "@core/schema/NewsOtp.js";
	import type { PublicArticle } from "@core/schema/PublicArticle.js";
	import { getGdprPath } from "@core/schema/paths/getGdprPath.js";
	import { createPromiseWithStatus } from "@core/utils/svelte/createPromiseWithStatus.svelte.js";
	import { getGlobalSettings } from "../contexts/getGlobalSettings.js";
	import { getGlobalTemplateProps } from "../contexts/getGlobalTemplateProps.js";
	import ArticleDetail from "./ArticleDetail.svelte";
	import type { OneTimeArticleDetailProps } from "./OneTimeArticleDetailProps.js";

	const all: OneTimeArticleDetailProps = $props();
	const { PUBLIC_GRAPHQL_ENDPOINT } = getGlobalSettings();
	const { tenant } = getGlobalTemplateProps();
	let feedback = $state<{ type: "success" | "warning"; text: string }>();

	let formSection = $state<HTMLElement>();

	function getOtpFromUrl(): string | null {
		if (typeof window === "undefined") {
			return null;
		}
		const params = new URLSearchParams(window.location.search);
		return params.get("otp");
	}

	function scrollToForm(): void {
		requestAnimationFrame(() => {
			formSection?.scrollIntoView({ behavior: "smooth" });
		});
	}

	const verifyOtp = createPromiseWithStatus(async function (otp: string): Promise<PublicArticle | undefined> {
		try {
			const result = await mutation<PublicArticle | null, VerifyNewsOtpInput & Record<string, unknown>>(
				PUBLIC_GRAPHQL_ENDPOINT,
				verifyNewsOtpMutation,
				{ otp, newsId: all.article.id },
			);
			if (result) {
				return result;
			} else {
				scrollToForm();
			}
		} catch (error) {
			scrollToForm();
			throw error;
		} finally {
			const url = new URL(window.location.href);
			url.searchParams.delete("otp");
			window.history.replaceState({}, "", url.toString());
		}
		return undefined;
	});

	const sendOtp = createPromiseWithStatus(async function (email: string): Promise<void> {
		if (!navigator.onLine) {
			throw new Error("Nejste připojeni k internetu.");
		}
		const now = new Date().toISOString();
		await mutation<boolean, { input: SendNewsOtpInput }>(PUBLIC_GRAPHQL_ENDPOINT, sendNewsOtpMutation, {
			input: {
				email,
				newsId: all.article.id,
				newsTitle: all.article.title,
				newsUrlSlug: all.article.urlSlug,
				gdprApprovedAt: now,
				marketingApprovedAt: now,
				locale: tenant.locale,
			},
		});
	});

	onMount(async () => {
		const otp = getOtpFromUrl();
		if (otp) {
			await verifyOtp.call(otp);
		}
	});

	async function submitOtpForm(event: SubmitEvent, form: HTMLFormElement): Promise<void> {
		event.preventDefault();
		const formData = new FormData(form);

		if (!navigator.onLine) {
			throw new Error("Nejste připojeni k internetu.");
		}
		await sendOtp.call(formData.get("email") as string);
		form.reset();
	}
	$effect(() => {
		if (sendOtp.value.status === "success") {
			feedback = {
				type: "success",
				text: "Na váš e-mail jsme odeslali odkaz pro zobrazení článku. Zkontrolujte prosím svou schránku.",
			};
		} else if (sendOtp.value.status === "error") {
			const detail = sendOtp.value.error instanceof Error ? sendOtp.value.error.message : String(sendOtp.value.error);
			feedback = {
				type: "warning",
				text: `Stala se chyba. Zkuste, prosím, formulář odeslat později. Detail chyby: ${detail}`,
			};
		}
	});
	$effect(() => {
		if (!feedback) {
			return;
		}

		const timeout = setTimeout(() => {
			feedback = undefined;
		}, 10_000);

		return (): void => {
			clearTimeout(timeout);
		};
	});

	const loading = $derived(sendOtp.value.status === "loading" || verifyOtp.value.status === "loading");
</script>

{#if verifyOtp.value.status === "success" && verifyOtp.value.result}
	<ArticleDetail {...all} article={verifyOtp.value.result} />
{:else}
	<ArticleStructuredData article={all.article} />
	<Layout ogImage={all.article.primaryImage}>
		<section class="pb-8 md:pb-16 lg:pb-20 full-width grid-container flex flex-col 2k:max-w-[116rem] 2k:mx-auto">
			<div class="flex flex-col gap-4 mb-10 max-w-[calc(72rem+var(--container-gap)] mx-auto">
				<div class="flex flex-col gap-4 max-w-screen-md mx-auto">
					<HugeHeading level={1} text={all.article.title} class="lg:!text-9xl !mb-0 mild" />
					{#if all.article.perex}
						<p class="text-[1.25rem]/[1.5] md:text-xl/[1.5] font-semibold max-w-screen-md">
							<Formatted text={all.article.perex} />
						</p>
					{/if}
					{#if all.article.primaryImage}
						<div class="bg-white rounded-lg p-4 mt-12">
							<Media
								class="rounded-lg"
								preload={{ fetchpriority: "high" }}
								source={all.article.primaryImage}
								alt="Úvodní fotka"
								width={736}
								height={414}
								sourcesets={{
									"<2xl": [414, 0],
									">2xl": [414, 0],
								}}
							/>
						</div>
					{/if}
				</div>
			</div>
			<ContentBlocks blocks={all.article.content} lightboxIdPerBlock align="center" />
			<section
				bind:this={formSection}
				class="bg-white/50 backdrop-blur-sm p-6 md:p-10 rounded-xl md:rounded-2xl container-8 mx-auto w-full"
			>
				<h2 class="mb-6 font-druk-cond tracking-[0.02em] text-[2rem] sm:text-[2.5rem] uppercase text-cmcsecondary">
					<Formatted text="Odemknout článek" />
				</h2>
				<p class="text-xs/[1.4] md:text-sm/[1.4] font-medium mb-6">
					<Formatted
						text="Pro zobrazení plného obsahu článku zadejte svůj e-mail. Na e-mail vám zašleme jednorázový odkaz."
					/>
				</p>
				<Form class="flex flex-col gap-4" onsubmit={submitOtpForm}>
					{#if feedback}
						<Alert type={feedback.type}>
							<Formatted text={feedback.text} />
						</Alert>
					{/if}
					<LabelledInput required type="email" name="email" value="" placeholder="Zadejte Váš e-mail">
						E-mail
					</LabelledInput>
					<LabelledInput required type="checkbox" name="gdpr" wrapperClass="flex items-center">
						<Formatted text="Souhlasím se l[{getGdprPath(tenant)}][[zpracováním osobních údajů]]" />
					</LabelledInput>
					<LabelledInput required type="checkbox" name="marketing" wrapperClass="flex items-center">
						<Formatted text="Souhlasím se zasíláním marketingových sdělení" />
					</LabelledInput>
					<Button
						icon={loading ? "loading" : "arrow"}
						disabled={loading}
						variant={ButtonVariant.Primary}
						class="gap-2 md:gap-4 w-fit mx-auto mt-2"
					>
						<Formatted text={sendOtp.value.status === "loading" ? "Odesílám" : "Odeslat"} />
					</Button>
					<RecaptchaDisclaimer />
				</Form>
			</section>
		</section>
	</Layout>
{/if}
